import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  OnDestroy,
  OnInit,
  Renderer2,
} from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { OKTA_AUTH } from '@okta/okta-angular';
import OktaAuth from '@okta/okta-auth-js';
import { catchError, Observable, take, throwError, timer } from 'rxjs';

import { AuthService } from 'src/app/core/services/auth';
import { ToggleComponent } from 'src/app/shared/components/toggle/toggle.component';
import { SharedModule } from 'src/app/shared/shared.module';

export interface LoginProviderResponse {
  type: string;
}
export function emailOrUsernameValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const v = (control.value || '').trim();
    if (!v) return { required: true };

    if (v.includes('@')) {
      const relaxedEmailRegex = /^[^\s@]+@[^\s@]+(\.[^\s@]+)?$/;
      return relaxedEmailRegex.test(v) ? null : { email: true };
    }

    return v.length >= 3 ? null : { usernameTooShort: true };
  };
}

@Component({
  templateUrl: './login.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    SharedModule,
    ToggleComponent,
    RouterLink,
  ],
})
export class LoginComponent implements OnInit, AfterViewInit, OnDestroy {
  title = 'MyPortal: Log In';

  passwordChanged: boolean = false;

  sessionExpired: boolean = false;

  loggedOut: boolean = false;

  usernameValid: boolean = false;

  showRegistrationMessage: boolean = false;

  isLoading: boolean = false;

  errorMessage: string;

  isLoading$: Observable<boolean>;

  errorMessage$: Observable<string>;

  messages = {
    usernameInput: '',
    emailInput: '',
    passwordInput: '',
    passwordChanged: 'Password changed!',
    sessionExpired: "Session expired. We've logged you out of your account.",
    loggedOut: 'You have successfully logged out.',
    registration:
      'Your registration was successful! Please log in to continue.',
  };

  currentStep: 'username' | 'password' = 'username';

  submittedUsername = false;

  submittedLogin = false;

  usernameFormGroup: FormGroup<{
    username: FormControl<string>;
  }> = new FormGroup({
    username: new FormControl('', {
      nonNullable: true,
      validators: [emailOrUsernameValidator()],
    }),
  });

  loginFormGroup = new FormGroup({
    username: new FormControl('', {
      nonNullable: true,
      validators: [emailOrUsernameValidator()],
    }),
    password: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required, Validators.minLength(8)],
    }),
    toggle: new FormControl(false, { nonNullable: true }),
  });

  userEmail: string;

  private inactivityTimer: NodeJS.Timeout = null;

  private authService = inject(AuthService);

  private route = inject(ActivatedRoute);

  private oktaAuth = inject<OktaAuth>(OKTA_AUTH);

  private renderer = inject(Renderer2);

  private cdr = inject(ChangeDetectorRef);

  ngOnInit(): void {
    this.route.fragment.subscribe((fragment: string | null) => {
      if (fragment?.includes('id_token')) {
        this.showRegistrationMessage = true;
      }
    });
    this.passwordChanged =
      this.route.snapshot.queryParamMap.get('passwordChanged') === 'true';
    this.sessionExpired =
      this.route.snapshot.queryParamMap.get('logout') === 'expired';
    this.loggedOut =
      this.route.snapshot.queryParamMap.get('logout') === 'manual';

    this.cdr.markForCheck();
    this.startInactivityTimer();
  }

  ngAfterViewInit(): void {
    this.renderer.selectRootElement('#usernameId').focus();
    this.clearErrorMessages();
  }

  ngOnDestroy(): void {
    if (this.inactivityTimer !== null) {
      clearInterval(this.inactivityTimer);
    }
  }

  controlInvalid(fg: FormGroup, name: string, submittedFlag: boolean): boolean {
    const c = fg.get(name);
    return !!c && c.invalid && (c.touched || c.dirty || submittedFlag);
  }

  login(): void {
    this.submittedLogin = true;

    if (this.loginFormGroup.invalid) {
      this.loginFormGroup.markAllAsTouched();

      const u = this.loginFormGroup.controls.username;
      const p = this.loginFormGroup.controls.password;

      if (u.errors) {
        if (u.errors.required)
          this.messages.usernameInput = 'Please enter your username.';
        else if (u.errors.email)
          this.messages.usernameInput = 'Please enter a valid email address.';
        else if (u.errors.usernameTooShort)
          this.messages.usernameInput =
            'Username must be at least 3 characters.';
        else this.messages.usernameInput = '';
      } else this.messages.usernameInput = '';

      if (p.errors) {
        if (p.errors.required)
          this.messages.passwordInput = 'Please enter a password.';
        else if (p.errors.minlength)
          this.messages.passwordInput =
            'Password must be at least 8 characters.';
        else this.messages.passwordInput = '';
      } else this.messages.passwordInput = '';

      return;
    }
    const username = this.loginFormGroup.controls.username.value.trim();
    const password = this.loginFormGroup.controls.password.value;
    const rememberMe = this.loginFormGroup.controls.toggle.value;

    this.isLoading = true;

    this.authService
      .login(username, password, rememberMe)
      .pipe(
        take(1),
        catchError((error: HttpErrorResponse) => {
          this.isLoading = false;
          const invalid = 'Incorrect username or password.';
          if (error.status === 401) this.messages.passwordInput = invalid;
          else if (error.status === 404) this.messages.usernameInput = invalid;
          else
            this.errorMessage =
              "We can't log you in right now. Please try again later.";
          this.clearErrorMessages();
          this.cdr.markForCheck();
          return throwError(() => error);
        }),
      )
      .subscribe(async (data) => {
        this.isLoading = false;
        this.cdr.markForCheck();
        if (
          data?.user?.OktaUserId__c &&
          data?.user?.Account?.Okta_Login_Enabled__c
        ) {
          await this.authService.loginWithOkta(data.user.OktaUsername__c);
        }
      });
  }

  checkLoginProvider(): void {
    this.submittedUsername = true;

    const username = this.usernameFormGroup.controls.username.value.trim();
    if (!username || this.usernameFormGroup.invalid) {
      this.messages.usernameInput = 'Please enter your username.';
      return;
    }
    this.isLoading = true;
    this.messages.usernameInput = '';

    this.authService
      .checkLoginProvider(username)
      .pipe(
        take(1),
        catchError((error: HttpErrorResponse) => {
          this.isLoading = false;
          this.messages.usernameInput =
            'Unable to verify username. Please try again later.';

          this.cdr.markForCheck();
          this.clearErrorMessages();
          return throwError(() => error);
        }),
      )
      .subscribe(async (data: LoginProviderResponse) => {
        this.isLoading = false;
        this.userEmail = this.usernameFormGroup.controls.username.value;

        if (data.type === 'IDP2') {
          await this.authService.loginWithOkta(username);
        } else {
          this.loginFormGroup.patchValue({
            username: this.usernameFormGroup.controls.username.value,
          });
          this.clearErrorMessages();
          this.currentStep = 'password';
        }
        this.cdr.markForCheck();
      });
  }

  goBack(): void {
    this.currentStep = 'username';
    this.renderer.selectRootElement('#usernameId').focus();
    this.cdr.markForCheck();
  }

  private clearErrorMessages(): void {
    timer(5000)
      .pipe(take(1))
      .subscribe(() => {
        this.errorMessage = null;
        this.passwordChanged = false;
        this.sessionExpired = false;
        this.loggedOut = false;
        this.showRegistrationMessage = false;
      });

    this.cdr.markForCheck();
  }

  private startInactivityTimer(): void {
    const fiveMinutes = 5 * 60 * 1000;
    this.inactivityTimer = setInterval(async () => {
      const oktaSession = await this.oktaAuth.session.get();
      if (oktaSession.status === 'ACTIVE') {
        await this.authService.signOut();
      }
    }, fiveMinutes);
    this.cdr.markForCheck();
  }
}
